/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { LinearProgress } from '@mui/material'
import { Seo, Footer, KoinosWordmark, KedemWordmark } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { buildItemListJsonLd, formatAmount } from '@utils'
import { useDisplayCurrency } from '@components/systems/monolith/useDisplayCurrency'
import dayjs from 'dayjs'

import { useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector, ApiGetDataStatus } from '@redux/index'
import { SeasonProduct, SeasonProductType } from '@schemas'
import { productLineList } from '@src/configs/products.config'

import pls8Image from '@public/images/monolith/koinos8-angle-full.png'
// 2026-09-15: 열대 식물 그림 배경 합성 컷(SITEMAP_PLAN §13.4)을 걷었다. Kedem 은 250714 컨셉 촬영의 누끼 컷을
// 페이지 지면 위에 PLS 8 과 같은 방식으로 세우고, POG 2 는 모듈 단독 컷을 무채색으로, Dual Switch 는 무지 배경 컷.
import kedem6Image from '@public/images/monolith/kedem6-top.webp'
import kedem4Image from '@public/images/monolith/kedem4-top.webp'
import amp2Image from '@public/images/pages/shop/amp2/gallery/amp2-module-mono.jpg'
import dsImage from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery2.jpg'

/**
 * Shop — Monolith(2안).
 *
 * 목업: claudeFiles/judahsWeb/homepage-mockups/2안-monolith/shop.html
 * 목업은 PLS 8 + Studio 두 행만 그렸지만, 실제 시즌 상품(KDM6/KDM4/AMP2/DS)이 API 로
 * 내려오므로 같은 "행" 레이아웃으로 전부 편다. PLS 8 은 항상 맨 위 — API 에 없으면
 * 티저 행으로 채운다(dev DB 미등록 상태 대비).
 * 2026-09-09: Studio 행 제거 — Shop 은 하드웨어 구매만 둔다. Studio 는 Support › Software 에 있다.
 *
 * 2026-09-09 사이트맵 §9: Shop 은 구매 한 장, 제품 소개는 /products 로 갔다. 행마다 `id={code}` 앵커가
 * 있어 상세의 "Buy" 가 `/shop#{code}` 로 착지한다. 장바구니 담기는 아직 상세(구 디자인 ShopOutline)에
 * 있고, 상세 4종의 Monolith 전환 때 이 행으로 옮긴다.
 */

/**
 * 단종 제품 코드 (2026-09-16). SSOT 는 `products.config.ts` 의 `discontinued` 이고
 * CMS 의 재고 상태(`sold_out`)와 무관하다 — "이번 시즌 품절"과 "더 안 만든다"는 다른 말이다.
 * 이 코드의 행은 목록 맨 아래 별도 구획으로 내려가고, 가격 없이 `/products/{code}` 로 간다.
 */
const DISCONTINUED_CODES = new Set(
	productLineList
		.flatMap(aLine => aLine.products)
		.filter(aProduct => aProduct.discontinued === true)
		.map(aProduct => aProduct.code.toUpperCase()),
)

const imageObj: { [code: string]: string } = {
	PLS8: pls8Image,
	KDM6: kedem6Image,
	KDM4: kedem4Image,
	AMP2: amp2Image,
	DS: dsImage,
}

/**
 * 표시명 SSOT — PRODUCT_NAMES.md §표. API 의 productName 을 그대로 쓰지 않는다:
 * DB 에는 "AirMIDI POG2"(공백 없음)로 들어 있어서 표기 규정("AirMIDI POG 2")과 어긋난다.
 * 제목·alt·aria-label·JSON-LD 전부 이 표를 거친다.
 */
const displayNameObj: { [code: string]: string } = {
	PLS8: 'Koinos PLS 8',
	KDM6: 'Kedem 6',
	KDM4: 'Kedem 4',
	AMP2: 'AirMIDI POG 2',
	DS: 'Dual Switch',
}

/** 행 본문 카피. seo.config 의 설명문과 같은 사실만 쓴다. */
const descriptionObj: { [code: string]: string } = {
	PLS8: 'Programmable loop switcher and MIDI foot controller. 8 loops, an 8.88-inch touch display, and the routing decisions moved onto the unit.',
	KDM6: 'Six-switch MIDI foot controller with a 3.5-inch touch display, AirMIDI wireless, and expression pedal input.',
	KDM4: 'The compact four-switch Kedem. Same touch display and AirMIDI wireless as the Kedem 6, sized for pedalboards that have run out of room.',
	AMP2: 'Preset switching for the Electro-Harmonix POG2 over wireless MIDI. Existing POG2 MIDI mods are wired; this one is not.',
	DS: 'Two momentary footswitches over a single TRS cable. Adds external footswitch control to pedals and amps that expose a TRS jack.',
}

/**
 * 행 제목(워드마크 포함) 크기 — 2열 배치라 40px 상한은 반칸 폭에서 넘친다.
 * PRODUCT_NAMES: Koinos 옆에 Kedem 이 서면 같은 size 여야 하므로 한 값으로 묶는다.
 */
const ROW_TITLE_SIZE = 'clamp(26px, 2.2vw, 34px)'

/** 배경이 없는 누끼 컷 — 보더·radius 없이 지면에 바로 세운다(PLS 8 과 같은 처방). 나머지는 사진 프레임. */
const CUTOUT_CODES = ['PLS8', 'KDM6', 'KDM4']

/**
 * 행에 보일 값. 프리오더 상품은 판매 단계(`phases`)를 본다 (2026-09-15 사용자 지시).
 *
 *   얼리버드가 있고 아직 안 끝났다(시작 전 포함) → 판매가에 줄 + 얼리버드가
 *   얼리버드가 없거나 끝났고 프리오더가 안 끝났다 → 판매가에 줄 + 프리오더가
 *   그 밖(단계 없음·전부 끝남·일반 상품)            → 할인 전 가격(seasonDiscountUsd)이 판매가보다 높으면 거기에 줄
 *
 * 기준단가(standardUsd)는 shop 에 나오지 않는다. 참고값이다 (PRICING §5, 2026-09-15).
 *
 * 판매가 = `seasonUsd`. 구매 패널(`MonolithBuyPanel`)이 Retail 로 부르는 값과 같다. 줄은 판매가가 더 비쌀 때만 긋는다.
 *
 * 원화(`*Krw`)는 같은 자리의 부가세 포함 원화다 (PRICING §7). 한국 방문자에게는 이 값만 보인다 —
 * 할인 전 원화는 DB 에 없어서 일반 상품의 줄은 USD 에서만 긋는다.
 */
const rowPrice = (
	aProduct: SeasonProductType,
	now: number,
): {
	struck: number | null
	value: number
	struckKrw: number | null
	valueKrw: number
} => {
	if (aProduct.preorderYn === 1) {
		const salePhases = (aProduct.phases ?? []).filter(aPhase => aPhase.kind === 'sale')
		const notEnded = (name: string) =>
			salePhases.find(
				aPhase =>
					aPhase.phase === name &&
					aPhase.usd > 0 &&
					(!aPhase.endAt || now <= dayjs(aPhase.endAt).valueOf()),
			)
		const lead = notEnded('earlybird') ?? notEnded('preorder')
		if (lead !== undefined) {
			return {
				struck: aProduct.seasonUsd > lead.usd ? aProduct.seasonUsd : null,
				value: lead.usd,
				struckKrw: aProduct.seasonKrw > lead.krw ? aProduct.seasonKrw : null,
				valueKrw: lead.krw,
			}
		}
	}
	return aProduct.seasonDiscountUsd > aProduct.seasonUsd
		? {
				struck: aProduct.seasonDiscountUsd,
				value: aProduct.seasonUsd,
				struckKrw: null,
				valueKrw: aProduct.seasonKrw,
		  }
		: {
				struck: null,
				value: aProduct.seasonUsd,
				struckKrw: null,
				valueKrw: aProduct.seasonKrw,
		  }
}

/**
 * 서버가 내려주는 시즌 상품 상태(SeasonProduct.status: not_start | expired | inactive |
 * preorder_closed | sold_out | active)를 그대로 쓴다. 2026-09-09 이전에는 active 가 아니면
 * 전부 "Sold out" 으로 찍어서, 시즌이 닫혀 끝난 상품(expired)까지 품절로 보였다.
 *
 * `preorder_closed` = 프리오더가 끝났고 재시작 판매가 아직 열리지 않은 구간. 그냥 품절이
 * 아니라 **다 팔렸다**는 사실이라 따로 말한다 (CATALOG_PLAN §7.4).
 */
const STATUS_LABEL: { [status: string]: string } = {
	active: 'In stock',
	sold_out: 'Sold out',
	preorder_closed: 'Pre-order sold out',
	preorder_not_started: 'Pre-order not open',
	expired: 'Unavailable',
	not_start: 'Not yet open',
	inactive: 'Unavailable',
}

const MonolithShopPage = () => {
	// hook
	const navigate = useNavigate()
	/*
		표시 통화 — 한국 방문자에게는 원화만 (PRICING §6 · §7.3, 2026-09-16). 달러를 작게라도 같이 두지
		않는다. 원화 값이 없는 행은 USD 로 되돌린다. 결제 통화는 배송국이 정한다 — 여기는 표시뿐이다.
	*/
	const currency = useDisplayCurrency()
	const location = useLocation()

	// redux state
	const status: ApiGetDataStatus = useAppSelector(state => state.shop.productListStatus)
	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)

	// PLS 8 을 맨 위로, 나머지는 productIndex 순.
	const productList: SeasonProductType[] = React.useMemo(() => {
		const res = [...seasonProductList]
		res.sort((a, b) => a.productIndex - b.productIndex)

		const pls8Idx = res.findIndex(
			aProduct => (aProduct.productCode ?? '').toUpperCase() === 'PLS8',
		)
		if (pls8Idx > 0) {
			const [pls8] = res.splice(pls8Idx, 1)
			res.unshift(pls8)
		}
		return res
	}, [seasonProductList])

	/** 판매 중인 행과 단종 행. 순서는 위에서 잡은 것을 그대로 쓴다 */
	const liveList = React.useMemo(
		() =>
			productList.filter(
				aProduct => !DISCONTINUED_CODES.has((aProduct.productCode ?? '').toUpperCase()),
			),
		[productList],
	)
	/**
	 * 단종 행은 판매 데이터와 무관하게 선다. CMS 에서 상품을 내리면 API 목록에서 사라지는데,
	 * 그렇다고 "예전에 이걸 만들었다"가 사라져야 할 이유는 없다. 값을 쓰지 않는 행이라
	 * 코드만 있으면 그려진다 — 시즌 상품이 남아 있으면 그 객체를 그대로 쓴다.
	 */
	const endedList = React.useMemo(() => {
		const byCode = new Map(
			productList.map(aProduct => [(aProduct.productCode ?? '').toUpperCase(), aProduct]),
		)
		return [...DISCONTINUED_CODES].map(aCode => {
			const found = byCode.get(aCode)
			if (found !== undefined) return found
			const placeholder = new SeasonProduct()
			placeholder.productCode = aCode
			return placeholder
		})
	}, [productList])

	const hasPls8 = productList.some(
		aProduct => (aProduct.productCode ?? '').toUpperCase() === 'PLS8',
	)

	// 구조화 데이터도 시즌 목록을 따른다. 아직 안 불러왔거나(프리렌더 중 API 실패 등) 비어 있으면
	// 정적 5종으로 떨어진다 — 색인에서 ItemList 가 통째로 사라지는 편이 더 나쁘다.
	const jsonLd = React.useMemo(() => {
		const items =
			productList.length > 0
				? productList.map(aProduct => {
						const code = (aProduct.productCode ?? '').toUpperCase()
						return {
							name: displayNameObj[code] ?? aProduct.productName,
							// 단종 제품은 거래 화면이 성립하지 않는다 — 제품 페이지가 그 자리를 받는다
							path: DISCONTINUED_CODES.has(code)
								? `/products/${code.toLowerCase()}`
								: `/shop/${code.toLowerCase()}`,
						}
				  })
				: [
						{ name: 'Koinos PLS 8', path: '/shop/pls8' },
						{ name: 'Kedem 6', path: '/products/kdm6' },
						{ name: 'Kedem 4', path: '/products/kdm4' },
						{ name: 'AirMIDI POG 2', path: '/shop/amp2' },
						{ name: 'Dual Switch', path: '/shop/ds' },
				  ]

		return buildItemListJsonLd(items)
	}, [productList])

	// `/shop#kdm6` 처럼 앵커로 들어오면 그 행으로. 라우터는 해시를 스크롤하지 않고 route.saga 가 맨 위로 올리므로
	// 목록이 그려진 뒤에 직접 내린다.
	React.useEffect(() => {
		if (location.hash === '' || ['init', 'loading'].includes(status)) return
		const el = document.getElementById(location.hash.slice(1))
		if (el !== null) {
			window.setTimeout(() => el.scrollIntoView({ block: 'start' }), 0)
		}
	}, [location.hash, status])

	// 2026-09-09: Shop 은 구매 목록이므로 행은 구매 상세(`/shop/{code}`)로 간다.
	// 소개는 `/products/{code}` 에 따로 있다.
	// 2026-09-16: 단종 제품만 예외다. 살 수 없는 제품에게 가격·수량·Add to cart 화면은
	// 성립하지 않으므로 제품 페이지로 보낸다.
	const onProductClick = (productCode: string) => {
		const code = (productCode ?? '').toUpperCase()
		if (DISCONTINUED_CODES.has(code)) {
			navigate(`/products/${code.toLowerCase()}`)
			return
		}
		navigate(`/shop/${code.toLowerCase()}`)
	}

	const renderRow = (aProduct: SeasonProductType) => {
		const code = (aProduct.productCode ?? '').toUpperCase()
		const isPls8 = code === 'PLS8'
		const isEnded = DISCONTINUED_CODES.has(code)
		// 사진이 없는 제품(새 코드가 DB 에만 올라온 경우)은 회색 플레이스홀더 대신 해칭 면으로 간다.
		const image = code in imageObj ? imageObj[code] : null
		const description = code in descriptionObj ? descriptionObj[code] : ''
		const name = displayNameObj[code] ?? aProduct.productName

		// 판매 상태 라벨. 고객 화면에는 "시즌" 개념을 노출하지 않는다(내부 용어) — 상태만 말한다.
		const notOpen = isPls8 && aProduct.seasonUsd <= 0
		const price = rowPrice(aProduct, Date.now())
		const showKrw = currency === 'KRW' && price.valueKrw > 0
		const struck = showKrw ? price.struckKrw : price.struck
		const value = showKrw ? price.valueKrw : price.value
		/** 가격표 문법 — `$1,000` · `₩1,500,000`, `.00` 없음 (`utils/Currency.ts`). 줄 그은 판매가가 먼저, 단계 값이 뒤 */
		const money = (amount: number): string =>
			formatAmount(showKrw ? 'KRW' : 'USD', amount)
		const stockLabel = notOpen
			? 'Not yet open'
			: aProduct.status === 'active' && aProduct.preorderYn === 1
			? 'Pre-order'
			: STATUS_LABEL[aProduct.status] ?? 'Unavailable'

		return (
			<View key={code} id={code.toLowerCase()} className="mnBand mnRow">
				<button
					type="button"
					className={`mnRowImageButton ${
						CUTOUT_CODES.includes(code) ? 'mnRowImageProduct' : 'mnRowImagePhoto'
					}`}
					onClick={() => onProductClick(aProduct.productCode)}
					aria-label={name}
				>
					{image !== null ? (
						<img src={image} alt={name} className="mnRowImage" />
					) : (
						/* LeWitt — 사진이 없는 면은 수직선이 코딩한다. Home 카탈로그의 Kedem 카드
						   (`.mnCardHatch`)와 같은 처방이다: 회색을 칠하지 않고 선을 반복한다. */
						<View className="mnRowImage mnRowHatch" aria-hidden="true" />
					)}
				</button>

				<View className="mnRowText">
					{isPls8 ? (
						<button
							type="button"
							className="mnRowTitleButton"
							onClick={() => onProductClick(aProduct.productCode)}
						>
							<KoinosWordmark as="span" size={ROW_TITLE_SIZE} />
						</button>
					) : (
						<button
							type="button"
							className="mnRowTitleButton mnRowTitle"
							onClick={() => onProductClick(aProduct.productCode)}
						>
							{/* Kedem 은 워드마크 규정(Iceland)으로 — 나머지(POG 2 · Dual Switch)는 규정 없음, 본문 서체 */}
							{code === 'KDM6' || code === 'KDM4' ? (
								<KedemWordmark
									as="span"
									model={code.replace('KDM', '')}
									size="clamp(28px, 2.8vw, 40px)"
								/>
							) : (
								name
							)}
						</button>
					)}

					{description !== '' && <p className="mnRowBody">{description}</p>}

					{/* 단종 행에는 값을 두지 않는다 — 값이 보이면 살 수 있는 것처럼 읽힌다 */}
					{!notOpen && !isEnded && (
						<View className="mnRowPrice">
							{struck !== null && (
								<span className="mnRowPriceStruck">{money(struck)}</span>
							)}
							<span className="mnRowPriceValue">{money(value)}</span>
						</View>
					)}

					<View className="mnRowActions">
						{isEnded ? (
							<button
								type="button"
								className="mnCtaGhost mnRowCta"
								onClick={() => onProductClick(aProduct.productCode)}
							>
								View product
							</button>
						) : notOpen ? (
							<button
								type="button"
								className="mnCta mnRowCta"
								onClick={() => navigate('/contact')}
							>
								Register interest
							</button>
						) : (
							<button
								type="button"
								className="mnCta mnRowCta"
								onClick={() => onProductClick(aProduct.productCode)}
							>
								{/* 이 행은 구매 화면(/shop/{code})으로 간다 — 제품 소개는 /products/{code} */}
								Buy
							</button>
						)}
						<span className="mnRowMeta">{isEnded ? 'Discontinued' : stockLabel}</span>
					</View>
				</View>
			</View>
		)
	}

	// API 에 PLS 8 이 없을 때의 정적 티저 행
	const renderPls8Teaser = () => (
		<View className="mnBand mnRow" id="pls8" key="pls8-teaser">
			<button
				type="button"
				className="mnRowImageButton mnRowImageProduct"
				onClick={() => navigate('/shop/pls8')}
				aria-label="Koinos PLS 8"
			>
				<img src={pls8Image} alt="Koinos PLS 8" className="mnRowImage" />
			</button>
			<View className="mnRowText">
				<button
					type="button"
					className="mnRowTitleButton"
					onClick={() => navigate('/shop/pls8')}
				>
					<KoinosWordmark as="span" size={ROW_TITLE_SIZE} />
				</button>
				<p className="mnRowBody">{descriptionObj.PLS8}</p>
				<View className="mnRowActions">
					<button
						type="button"
						className="mnCta mnRowCta"
						onClick={() => navigate('/contact')}
					>
						Register interest
					</button>
					<span className="mnRowMeta">Not yet open</span>
				</View>
			</View>
		</View>
	)

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">Shop</h1>
				</View>

				{['init', 'loading'].includes(status) ? (
					<View className="mnBand mnLoading">
						<LinearProgress className="mnLoadingBar" />
					</View>
				) : (
					<>
						<View className="mnShopGrid">
							{!hasPls8 && renderPls8Teaser()}
							{liveList.map(renderRow)}
						</View>

						{/* 단종 구획 — 목록과 같은 행 문법 그대로, 헤어라인 하나로 끊어 아래에 둔다 */}
						{endedList.length > 0 && (
							<View className="mnEndedSection">
								<View className="mnEndedHead">
									<span className="mnKicker">Discontinued</span>
									<p className="mnEndedLine">The first generation sold out.</p>
								</View>
								<View className="mnShopGrid">{endedList.map(renderRow)}</View>
							</View>
						)}
					</>
				)}
			</View>

			<Footer />
		</View>
	)
}

export default MonolithShopPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	/**
	 * Judd — 페이지 헤더는 Support · Products · Manuals · Software · About 과 같은 유닛이다.
	 * 값은 원래부터 같았고 클래스 이름만 이 페이지 전용이었다(`mnShopHeader`/`mnShopTitle`).
	 * 이름을 공용 이름으로 맞춘다 — 렌더 결과는 바뀌지 않는다.
	 * Shop 만 리드 문장이 없어 4:8 그리드를 두지 않는다(한 칸짜리 그리드는 제목 폭을 4열로 묶는다).
	 */
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
	},

	'.mnLoading': {
		display: 'flex',
		justifyContent: 'center',

		'.mnLoadingBar': {
			width: 320,
			'& .MuiLinearProgress-bar': { background: mono.ink },
			background: mono.line,
		},
	},

	/**
	 * 2026-09-09: 상품을 한 줄에 두 개씩. 밴드 안에 박스를 두지 않는다는 조항(§4)은 그대로다 —
	 * 카드 배경·보더를 얹지 않고, 셀마다 헤어라인만 남겨 두 칸이 한 줄의 규칙선을 이룬다.
	 * 반칸 폭에서는 이미지+텍스트 좌우 배치가 좁아지므로 셀 안은 이미지 위 / 텍스트 아래로 쌓는다.
	 */
	'.mnShopGrid': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		columnGap: 'clamp(32px, 4.4vw, 64px)',

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
		},
	},

	/** 단종 구획 — 판매 목록과 규칙선을 공유하되 헤어라인 하나로 층을 나눈다 */
	'.mnEndedSection': {
		borderTop: `1px solid ${mono.line}`,
		marginTop: 'clamp(24px, 3vw, 48px)',
		paddingTop: 'clamp(40px, 4.4vw, 64px)',

		'.mnEndedHead': {
			display: 'flex',
			flexDirection: 'column',
			gap: 12,
			maxWidth: 560,
		},
		'.mnEndedLine': {
			fontSize: 16,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
		},
	},

	'.mnRow': {
		scrollMarginTop: 72,
		display: 'flex',
		flexDirection: 'column',
		gap: 'clamp(24px, 2.4vw, 32px)',
		padding: 'clamp(40px, 4.4vw, 64px) 0',

		'.mnRowImageButton': {
			display: 'block',
			width: '100%',
			padding: 0,
			border: 0,
			background: 'transparent',
			cursor: 'pointer',

			'.mnRowImage': {
				width: '100%',
				display: 'block',
			},

			'&.mnRowImageProduct .mnRowImage': {
				aspectRatio: '1.45 / 1',
				objectFit: 'cover',
				filter: 'drop-shadow(0 24px 40px rgba(20,24,32,0.14))',
			},
			'&.mnRowImagePhoto .mnRowImage': {
				aspectRatio: '1.45 / 1',
				objectFit: 'cover',
				border: `1px solid ${mono.line}`,
				borderRadius: 8,
				background: mono.surface,
			},
			/**
			 * 사진이 없는 자리. 굵기 1px·간격 5px 수직선 — Home `.mnCardHatch` 와 같은 값이다.
			 * (해칭 생성 헬퍼는 아직 Home.page.tsx 안에 있다. 공용 토큰으로 올리는 건 별건.)
			 */
			'.mnRowHatch': {
				backgroundImage: `repeating-linear-gradient(90deg, ${mono.lineInput} 0 1px, transparent 1px 5px)`,
			},
			'&.mnRowImageShot': {
				cursor: 'default',
				'.mnRowImage': {
					border: `1px solid ${mono.line}`,
					borderRadius: 8,
				},
			},
		},

		'.mnRowText': {
			flex: 1,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 20,
		},

		/**
		 * 제목 자리는 고정 높이 상자다 — Home 카탈로그 `.mnCardTitle` 과 같은 처방(§4 카드 제목).
		 * 워드마크마다 폰트 메트릭이 달라(Koinos 32px · Kedem(Iceland ×1.43) 61px · 본문 서체 35px)
		 * 두 칸을 나란히 두면 아래 본문·가격 줄이 서로 어긋난다. 상자를 고정해 한 줄의 규칙선을 지킨다.
		 */
		'.mnRowTitleButton': {
			padding: 0,
			border: 0,
			background: 'transparent',
			cursor: 'pointer',
			color: mono.ink,
			textAlign: 'left',
			fontFamily: 'inherit',
			minHeight: 64,
			display: 'flex',
			alignItems: 'center',
		},

		'.mnRowTitle': {
			fontSize: ROW_TITLE_SIZE,
			fontWeight: 400,
			lineHeight: 1.1,
			color: mono.ink,
			'&.mnRowTitleThin': { fontWeight: 300 },
		},

		'.mnRowBody': {
			fontSize: 16,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
		},

		'.mnRowPrice': {
			display: 'flex',
			alignItems: 'baseline',
			gap: 14,

			'.mnRowPriceStruck': {
				fontSize: 15,
				fontWeight: 300,
				color: mono.faint,
				textDecoration: 'line-through',
			},
			'.mnRowPriceValue': {
				fontSize: 22,
				fontWeight: 300,
				color: mono.ink,
			},
		},

		'.mnRowActions': {
			display: 'flex',
			alignItems: 'center',
			gap: 24,
			marginTop: 'auto',
			paddingTop: 6,
			flexWrap: 'wrap',

			'.mnRowCta': {
				height: 50,
				padding: '0 36px',
				fontSize: 14,
			},

			'.mnRowMeta': {
				fontSize: 13,
				letterSpacing: '.18em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
		},
	},
})
